// import React, { Component, ErrorInfo, ReactNode } from 'react';
// import { Box, Typography, Button, Paper } from '@mui/material';
// import { Refresh as RefreshIcon } from '@mui/icons-material';

// interface Props {
//   children: ReactNode;
//   fallback?: ReactNode;
// }

// interface State {
//   hasError: boolean;
//   error?: Error;
//   errorInfo?: ErrorInfo;
// }

// class ErrorBoundary extends Component<Props, State> {
//   constructor(props: Props) {
//     super(props);
//     this.state = { hasError: false };
//   }

//   static getDerivedStateFromError(error: Error): State {
//     // Update state so the next render will show the fallback UI
//     return { hasError: true, error };
//   }

//   componentDidCatch(error: Error, errorInfo: ErrorInfo) {
//     // Log error details
//     console.error('ErrorBoundary caught an error:', error, errorInfo);
    
//     this.setState({
//       error,
//       errorInfo
//     });

//     // You can also log the error to an error reporting service here
//     // Example: logErrorToService(error, errorInfo);
//   }

//   handleRefresh = () => {
//     window.location.reload();
//   };

//   render() {
//     if (this.state.hasError) {
//       // Custom fallback UI
//       if (this.props.fallback) {
//         return this.props.fallback;
//       }

//       // Default error UI
//       return (
//         <Box
//           sx={{
//             display: 'flex',
//             flexDirection: 'column',
//             alignItems: 'center',
//             justifyContent: 'center',
//             minHeight: '100vh',
//             padding: 3,
//             backgroundColor: 'background.default'
//           }}
//         >
//           <Paper
//             elevation={3}
//             sx={{
//               padding: 4,
//               maxWidth: 500,
//               textAlign: 'center',
//               borderRadius: 2
//             }}
//           >
//             <Typography variant="h4" color="error" gutterBottom>
//               ¡Oops! Algo salió mal
//             </Typography>
            
//             <Typography variant="body1" color="text.secondary" paragraph>
//               La aplicación encontró un error inesperado. Por favor, intenta recargar la página.
//             </Typography>

//             {process.env.NODE_ENV === 'development' && this.state.error && (
//               <Box
//                 sx={{
//                   mt: 2,
//                   p: 2,
//                   backgroundColor: 'error.light',
//                   borderRadius: 1,
//                   textAlign: 'left'
//                 }}
//               >
//                 <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem' }}>
//                   {this.state.error.toString()}
//                 </Typography>
//               </Box>
//             )}

//             <Button
//               variant="contained"
//               startIcon={<RefreshIcon />}
//               onClick={this.handleRefresh}
//               sx={{ mt: 2 }}
//             >
//               Recargar página
//             </Button>
//           </Paper>
//         </Box>
//       );
//     }

//     return this.props.children;
//   }
// }

// export default ErrorBoundary;
import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Box, Typography, Button, Paper } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  eventId?: string; // useful to show Sentry event reference
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Optional: attach any global/app state you control (examples)
    const appContext = {
      url: window.location.href,
      userAgent: navigator.userAgent,
      // If you have a store, attach a sanitized snapshot here (avoid secrets/base64)
      // redux: store.getState(),
      // route: window.location.pathname,
    };

    const eventId = Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
        app: appContext,
      },
      extra: {
        // raw stack trace; Sentry will also parse it into frames
        stack: error.stack,
        // keep ErrorInfo too (React-specific)
        errorInfo,
      },
      tags: {
        boundary: "ErrorBoundary",
      },
    });

    this.setState({ error, errorInfo, eventId });
  }

  handleRefresh = () => window.location.reload();

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", p: 3 }}>
          <Paper elevation={3} sx={{ p: 4, maxWidth: 500, textAlign: "center", borderRadius: 2 }}>
            <Typography variant="h4" color="error" gutterBottom>
              ¡Oops! Algo salió mal
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
              La aplicación encontró un error inesperado. Por favor, intenta recargar la página.
            </Typography>

            {/* Show stack in dev only */}
            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: "error.light", borderRadius: 1, textAlign: "left" }}>
                <Typography variant="caption" component="pre" sx={{ fontSize: "0.75rem", whiteSpace: "pre-wrap" }}>
                  {this.state.error.stack || this.state.error.toString()}
                </Typography>
              </Box>
            )}

            <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleRefresh} sx={{ mt: 2 }}>
              Recargar página
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
